import React, { useContext, useEffect, useState, useRef } from 'react';
import api from '../api';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import CheckoutSteps from '../components/general/CheckoutSteps';
import { useTranslation } from 'react-i18next';
import { Container, Card, Dropdown, Modal } from 'react-bootstrap';
import { Autocomplete } from '@react-google-maps/api';
import i18n from '../i18n/i18n';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { formatEuro } from '../utils';
import { toast } from 'react-toastify';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { findShippingZone, isSameAddress } from '../utils/checkout';
import {
  validateAndNormalizePhone,
  formatCountryLabel,
  getCountryDisplayName,
  flattenZoneCountries,
  filterCountries,
  parsePlaceAddress,
  compareGeocodedAddress,
  checkAddressSubmission,
  addressesToSave,
} from '../utils/addressForm';

export default function ShippingAddressScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen
  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives access to the state of the context (in this case the Store) and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const {
    userInfo,
    cart: { shippingAddress, billingAddress, cartItems },
  } = state; // gets the shippingAddress and billingAddress out of the application state (wich is either the address, if the user already entered his address before and is therefor stored in his localStorage, or an empty objcet) <- the logic of that can be found in the Store.js file

  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState({});
  const [selectedShippingCountry, setSelectedShippingCountry] = useState({});
  const [selectedBillingCountry, setSelectedBillingCountry] = useState({});
  const [shippingCountrySearchTerm, setShippingCountrySearchTerm] =
    useState('');
  const [billingCountrySearchTerm, setBillingCountrySearchTerm] = useState('');
  const [showShippingCountryList, setShowShippingCountryList] = useState(false);
  const [showBillingCountryList, setShowBillingCountryList] = useState(false);
  const shippingCountrySelectionRef = useRef(null); // ref that will wrap the input and dropdown
  const billingCountrySelectionRef = useRef(null); // ref that will wrap the input and dropdown
  const [loadingZones, setLoadingZones] = useState(false);
  const [zonesReloadCount, setZonesReloadCount] = useState(0); // bumped by the retry button to re-run the zones fetch
  const [errorZones, setErrorZones] = useState(''); // set when the zones (and therefore the country lists) could not be loaded
  const autocompleteShippingRef = useRef(null);
  const autocompleteBillingRef = useRef(null);
  const selectedLanguage = i18n.resolvedLanguage;
  const [validationBypassed, setValidationBypassed] = useState(false);

  // Shipping Address:
  const [fullNameShipping, setFullNameShipping] = useState(
    shippingAddress.fullName || ''
  );
  const [companyNameShipping, setCompanyNameShipping] = useState(
    shippingAddress.companyName || ''
  );
  const [streetShipping, setStreetShipping] = useState(
    shippingAddress.street || ''
  );
  const [houseNumberShipping, setHouseNumberShipping] = useState(
    shippingAddress.houseNumber || ''
  );
  const [postalCodeShipping, setPostalCodeShipping] = useState(
    shippingAddress.postalCode || ''
  );
  const [cityShipping, setCityShipping] = useState(shippingAddress.city || '');
  const [countryShipping, setCountryShipping] = useState(
    shippingAddress.country || ''
  );
  const [countryIdShipping, setCountryIdShipping] = useState(
    shippingAddress.countryId || ''
  );
  const [emailShipping, setEmailShipping] = useState(
    shippingAddress.email || userInfo?.email || ''
  );
  const [phoneShipping, setPhoneShipping] = useState(
    shippingAddress.phone || ''
  );

  // Billing Address:
  const [fullNameBilling, setFullNameBilling] = useState(
    billingAddress.fullName || ''
  );
  const [companyNameBilling, setCompanyNameBilling] = useState(
    billingAddress.companyName || ''
  );
  const [streetBilling, setStreetBilling] = useState(
    billingAddress.street || ''
  );
  const [houseNumberBilling, setHouseNumberBilling] = useState(
    billingAddress.houseNumber || ''
  );
  const [postalCodeBilling, setPostalCodeBilling] = useState(
    billingAddress.postalCode || ''
  );
  const [cityBilling, setCityBilling] = useState(billingAddress.city || '');
  const [countryBilling, setCountryBilling] = useState(
    billingAddress.country || ''
  );
  const [countryIdBilling, setCountryIdBilling] = useState(
    billingAddress.countryId || ''
  );
  const [emailBilling, setEmailBilling] = useState(
    billingAddress.email || userInfo?.email || ''
  );
  const [phoneBilling, setPhoneBilling] = useState(billingAddress.phone || '');

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

  const [validationErrorsShipping, setValidationErrorsShipping] = useState({
    street: false,
    houseNumber: false,
    postalCode: false,
    city: false,
    country: false,
  });

  const [validationErrorsBilling, setValidationErrorsBilling] = useState({
    street: false,
    houseNumber: false,
    postalCode: false,
    city: false,
    country: false,
  });

  const [showAddressValidationModal, setShowAddressValidationModal] =
    useState(false); // State to track if our “address not found” modal is open
  const [addressErrorType, setAddressErrorType] = useState(''); // To record whether the shipping, billing, or both addresses have errors

  const [phoneCountryShipping, setPhoneCountryShipping] = useState('de');
  const [phoneCountryBilling, setPhoneCountryBilling] = useState('de');
  const [phoneErrorShipping, setPhoneErrorShipping] = useState('');
  const [phoneErrorBilling, setPhoneErrorBilling] = useState('');

  const normalizedPhonesRef = useRef({ shipping: '', billing: '' }); // stores the validated/normalized phone numbers (E.164) across renders without causing re-renders

  useEffect(() => {
    if (!userInfo) {
      navigate('/signup?redirect=/shipping-address'); // if the user isn't signed in, he can't access the AddressScreen and instead will be redirected to the SigninScreen. After sign in, the user will than be redirected to the AddressScreen
    } else if (cartItems.length === 0) {
      navigate('/'); // if there are no items in the cart, the user cant access this screen
    }
  }, [userInfo, cartItems, navigate]);

  useEffect(() => {
    setBillingSameAsShipping(
      isSameAddress(
        {
          fullName: fullNameShipping,
          companyName: companyNameShipping,
          street: streetShipping,
          houseNumber: houseNumberShipping,
          postalCode: postalCodeShipping,
          city: cityShipping,
          country: countryShipping,
          phone: phoneShipping,
          email: emailShipping,
        },
        {
          fullName: fullNameBilling,
          companyName: companyNameBilling,
          street: streetBilling,
          houseNumber: houseNumberBilling,
          postalCode: postalCodeBilling,
          city: cityBilling,
          country: countryBilling,
          phone: phoneBilling,
          email: emailBilling,
        }
      ) && countryIdShipping === countryIdBilling // the checkout's comparison of the stored addresses, plus the country id
    );
  }, []); // Compare each field in billing and shipping addresses and set the default billingSameAsShipping state

  useEffect(() => {
    const fallbackEmail = userInfo?.email || '';

    // only prefill if the user hasn't typed anything / no email stored yet:
    if (!emailShipping && fallbackEmail) {
      setEmailShipping(fallbackEmail);
    }
    if (!emailBilling && fallbackEmail) {
      setEmailBilling(fallbackEmail);
    }
  }, [userInfo?.email]); // this useEffect will fill email input fields once userInfo loads. This is needed, because userInfo comes in after initial render and the useState(...) initializers for setEmailShipping and setEmailBilling may have had no access to userInfo yet.

  useEffect(() => {
    const fetchZones = async () => {
      setLoadingZones(true);
      setErrorZones(''); // clear a previous failure, so a retry can recover

      let zoneData;

      try {
        const { data } = await api.get('/api/zones?pageSize=1000'); // checkout needs every zone, not the first page — /api/zones defaults to 20 per page
        zoneData = data.zones;
      } catch (err) {
        // the request itself failed, so there are no countries to choose from and the form cannot be completed:
        console.error('Error fetching zones:', err);
        setErrorZones('shipping-address-screen-zones-error'); // store the key, not the translated text, so the message follows a language switch
        setLoadingZones(false);
        return;
      }

      try {
        setZones(zoneData);

        if (countryIdShipping) {
          const match = findShippingZone(zoneData, countryIdShipping);

          if (match) {
            setShippingCountrySearchTerm(
              formatCountryLabel(match.country.names[selectedLanguage])
            );
            setSelectedZone(match.zone);
            setSelectedShippingCountry(match.country);
          } else {
            // No match
            setCountryIdShipping('');
            setSelectedZone({});
            setSelectedShippingCountry({});
          }
        }

        if (countryIdBilling) {
          const match = findShippingZone(zoneData, countryIdBilling);

          if (match) {
            setBillingCountrySearchTerm(
              formatCountryLabel(match.country.names[selectedLanguage])
            );
            setSelectedBillingCountry(match.country);
          } else {
            // No match
            setCountryIdBilling('');
            setSelectedBillingCountry({});
          }
        }

        setLoadingZones(false);
      } catch (err) {
        // the zones did arrive, so the country lists work — preselecting the user's country failed, which is not worth blocking the form for:
        console.error('Error preselecting the zone country:', err);
        setLoadingZones(false);
      }
    };
    fetchZones();
  }, [zonesReloadCount]); // fetch zone documents from the db and set selected zone, if countryId has been found in the users shippingAddress and billingAddress

  useEffect(() => {
    if (
      selectedShippingCountry &&
      selectedShippingCountry.names &&
      selectedShippingCountry.names[selectedLanguage]
    ) {
      setShippingCountrySearchTerm(
        formatCountryLabel(selectedShippingCountry.names[selectedLanguage])
      );
    }

    if (
      selectedBillingCountry &&
      selectedBillingCountry.names &&
      selectedBillingCountry.names[selectedLanguage]
    ) {
      setBillingCountrySearchTerm(
        formatCountryLabel(selectedBillingCountry.names[selectedLanguage])
      );
    }
  }, [selectedLanguage]); // update the search terms according to the selected language

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        shippingCountrySelectionRef.current &&
        !shippingCountrySelectionRef.current.contains(event.target)
      ) {
        if (Object.keys(selectedShippingCountry).length === 0) {
          // if the user has not selected a country previously, set the search term to an empty string:
          setShippingCountrySearchTerm('');
        } else {
          // if the user has already selected a country previously, set the search term to the selected country:
          setShippingCountrySearchTerm(
            formatCountryLabel(selectedShippingCountry.names[selectedLanguage])
          );
        }

        setShowShippingCountryList(false); // close the countries dropdown
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedShippingCountry, selectedLanguage]); // Close the dropdown if a click occurs outside the container

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        billingCountrySelectionRef.current &&
        !billingCountrySelectionRef.current.contains(event.target)
      ) {
        if (Object.keys(selectedBillingCountry).length === 0) {
          // if the user has not selected a country previously, set the search term to an empty string:
          setBillingCountrySearchTerm('');
        } else {
          // if the user has already selected a country previously, set the search term to the selected country:
          setBillingCountrySearchTerm(
            formatCountryLabel(selectedBillingCountry.names[selectedLanguage])
          );
        }

        setShowBillingCountryList(false); // close the countries dropdown
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedBillingCountry, selectedLanguage]); // Close the dropdown if a click occurs outside the container

  const allCountries = React.useMemo(
    () => flattenZoneCountries(zones, selectedLanguage),
    [zones, selectedLanguage]
  ); // Flatten all countries from zones once zones are loaded

  const filteredShippingCountries = React.useMemo(
    () => filterCountries(allCountries, shippingCountrySearchTerm),
    [allCountries, shippingCountrySearchTerm]
  ); // Filtered list of countries based on user's input in the current language

  const filteredBillingCountries = React.useMemo(
    () => filterCountries(allCountries, billingCountrySearchTerm),
    [allCountries, billingCountrySearchTerm]
  ); // Filtered list of countries based on user's input in the current language

  const handleShippingCountrySelection = (selectedId) => {
    const matchedCountry = allCountries.find((c) => c._id === selectedId);

    if (matchedCountry) {
      const matchedZone = zones.find((z) => z._id === matchedCountry.zoneId);

      setSelectedShippingCountry(matchedCountry);
      setSelectedZone(matchedZone || {});

      setCountryShipping(
        getCountryDisplayName(matchedCountry, selectedLanguage)
      );

      setCountryIdShipping(matchedCountry._id || '');

      setShowShippingCountryList(false); // hide the dropdown once a country is selected

      setShippingCountrySearchTerm(
        formatCountryLabel(matchedCountry.names?.[selectedLanguage] || {})
      );
    }
  }; // This is triggered whenever a user selects an option from the dropdown

  const handleBillingCountrySelection = (selectedId) => {
    const matchedCountry = allCountries.find((c) => c._id === selectedId);

    if (matchedCountry) {
      setSelectedBillingCountry(matchedCountry);

      setCountryBilling(
        getCountryDisplayName(matchedCountry, selectedLanguage)
      );
      setCountryIdBilling(matchedCountry._id || '');
      setShowBillingCountryList(false); // hide the dropdown once a country is selected

      setBillingCountrySearchTerm(
        formatCountryLabel(matchedCountry.names?.[selectedLanguage] || {})
      );
    }
  }; // This is triggered whenever a user selects an option from the dropdown

  const handleShippingCountrySearchChange = (e) => {
    setShippingCountrySearchTerm(e.target.value);
    setShowShippingCountryList(true); // Show list if user is actively typing
  }; // Called when user types in the search box

  const handleBillingCountrySearchChange = (e) => {
    setBillingCountrySearchTerm(e.target.value);
    setShowBillingCountryList(true); // Show list if user is actively typing
  }; // Called when user types in the search box

  const handleSelectShippingCountry = (countryId) => {
    handleShippingCountrySelection(countryId);
    setShowShippingCountryList(false);
  }; // Internal wrapper to handle selection and then close the dropdown

  const handleSelectBillingCountry = (countryId) => {
    handleBillingCountrySelection(countryId);
    setShowBillingCountryList(false);
  }; // Internal wrapper to handle selection and then close the dropdown

  const handlePlaceChangedShipping = () => {
    const place = autocompleteShippingRef.current.getPlace();
    if (!place) return;

    const addressComponents = place.address_components;
    if (!addressComponents) return;

    const { street, houseNumber, city, postalCode } =
      parsePlaceAddress(addressComponents);

    setStreetShipping(street);
    setHouseNumberShipping(houseNumber);
    setCityShipping(city);
    setPostalCodeShipping(postalCode);
  };

  const handlePlaceChangedBilling = () => {
    const place = autocompleteBillingRef.current.getPlace();
    if (!place) return;

    const addressComponents = place.address_components;
    if (!addressComponents) return;

    const { street, houseNumber, city, postalCode } =
      parsePlaceAddress(addressComponents);

    setStreetBilling(street);
    setHouseNumberBilling(houseNumber);
    setCityBilling(city);
    setPostalCodeBilling(postalCode);
  };

  async function validateAddress({
    street,
    houseNumber,
    city,
    postalCode,
    countryCode,
  }) {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps) {
        return resolve({ error: 'API_NOT_LOADED' });
      }

      const geocoder = new window.google.maps.Geocoder();
      const addressString = `${houseNumber} ${street}, ${city}, ${postalCode}, ${countryCode}`;

      geocoder.geocode({ address: addressString }, (results, status) => {
        if (status === 'OK' && results.length > 0) {
          resolve(
            compareGeocodedAddress(
              { street, houseNumber, city, postalCode, countryCode },
              results[0].address_components
            )
          );
        } else if (status === 'OVER_QUERY_LIMIT') {
          resolve({ error: 'QUOTA_EXCEEDED' });
        } else {
          resolve({ error: 'VALIDATION_FAILED' });
        }
      });
    });
  } // this function validates an address using the Google Geocoding API

  const submitHandler = async (e) => {
    e.preventDefault();

    // reset the phone number error messages:
    setPhoneErrorShipping('');
    setPhoneErrorBilling('');

    await checkAddressSubmission(
      {
        shipping: {
          phone: phoneShipping,
          phoneCountry: phoneCountryShipping,
          street: streetShipping,
          houseNumber: houseNumberShipping,
          city: cityShipping,
          postalCode: postalCodeShipping,
          country: selectedShippingCountry,
        },
        billing: {
          phone: phoneBilling,
          phoneCountry: phoneCountryBilling,
          street: streetBilling,
          houseNumber: houseNumberBilling,
          city: cityBilling,
          postalCode: postalCodeBilling,
          country: selectedBillingCountry,
        },
        billingSameAsShipping,
        validationBypassed,
      },
      {
        validateAddress,
        storePhones: (phones) => {
          normalizedPhonesRef.current = phones; // store the validated and normalized phone numbers inside normalizedPhonesRef, so that they can be accessed from inside the proceedSubmission function
        },
        onPhoneError: (side, error) => {
          const message =
            error === 'REQUIRED'
              ? t('shipping-address-screen-phone-required')
              : t('shipping-address-screen-phone-invalid'); // display an error message in the form
          if (side === 'shipping') setPhoneErrorShipping(message);
          else setPhoneErrorBilling(message);
        },
        onCountryMissing: (side) =>
          toast.error(
            t(
              side === 'shipping'
                ? 'shipping-address-screen-select-country-first'
                : 'shipping-address-screen-select-billing-country-first'
            )
          ),
        onApiError: () => {
          setAddressErrorType('apiError'); // Set a custom error type to show the special modal message
          setShowAddressValidationModal(true);
        },
        onValidation: (side, result) => {
          if (side === 'shipping') setValidationErrorsShipping(result);
          else setValidationErrorsBilling(result);
        },
        onAddressErrors: (errorType) => {
          setAddressErrorType(errorType);
          setShowAddressValidationModal(true);
        },
        proceed: proceedSubmission,
      }
    ); // the phones, the countries, the bypass and the address validation, in that order
  };

  const proceedSubmission = () => {
    const {
      shippingAddress: finalShippingAddress,
      billingAddress: finalBillingAddress,
    } = addressesToSave({
      shipping: {
        fullName: fullNameShipping,
        companyName: companyNameShipping,
        street: streetShipping,
        houseNumber: houseNumberShipping,
        city: cityShipping,
        postalCode: postalCodeShipping,
        country: countryShipping,
        countryId: countryIdShipping,
        selectedCountry: selectedShippingCountry,
        email: emailShipping,
      },
      billing: {
        fullName: fullNameBilling,
        companyName: companyNameBilling,
        street: streetBilling,
        houseNumber: houseNumberBilling,
        city: cityBilling,
        postalCode: postalCodeBilling,
        country: countryBilling,
        countryId: countryIdBilling,
        selectedCountry: selectedBillingCountry,
        email: emailBilling,
      },
      billingSameAsShipping,
      phones: normalizedPhonesRef.current, // the validated and normalized phone numbers
      allCountries,
      selectedLanguage,
    }); // the addresses as the store and localStorage keep them

    ctxDispatch({
      type: 'SAVE_SHIPPING_ADDRESS',
      payload: finalShippingAddress,
    }); // Dispatch shipping address to store
    ctxDispatch({
      type: 'SAVE_BILLING_ADDRESS',
      payload: finalBillingAddress,
    }); // Dispatch billing address to store

    // Save to localStorage:
    localStorage.setItem(
      'shippingAddress',
      JSON.stringify(finalShippingAddress)
    );
    localStorage.setItem('billingAddress', JSON.stringify(finalBillingAddress));

    navigate('/payment'); // navigate to payment screen
  }; // a helper to actually do the dispatch + localStorage + navigate steps

  return (
    <Container className="checkout-steps-container">
      {loadingZones ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : errorZones ? (
        <div className="zones-error-container">
          <MessageBox className="danger-alert">{t(errorZones)}</MessageBox>

          <Button
            variant="dark"
            type="button"
            onClick={() => setZonesReloadCount((count) => count + 1)}
          >
            {t('general-try-again')}
          </Button>
        </div>
      ) : (
        <div>
          <title>{t('shipping-address-screen-helmet')}</title>

          <CheckoutSteps step1 step2></CheckoutSteps>

          <div className="container small-container">
            {selectedZone && selectedShippingCountry && countryIdShipping ? (
              <Card className="shipping-costs-card">
                <Card.Body>
                  <Card.Title>
                    <strong>
                      {t('shipping-address-screen-shipping-costs')}
                    </strong>
                    {/* Currently disabled, because the shipping provider is not know at this time yet:
                      <img
                        src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/DHL.png"
                        alt="DHL"
                      />
                    */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      className="bi bi-truck"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-3.998-.085A1.5 1.5 0 0 1 0 10.5zm1.294 7.456A2 2 0 0 1 4.732 11h5.536a2 2 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456M12 10a2 2 0 0 1 1.732 1h.768a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2" />
                    </svg>
                  </Card.Title>
                  <div className="divider"></div>
                  <Card.Text>
                    <span>
                      {selectedLanguage === 'de'
                        ? selectedZone?.names?.de
                        : selectedZone?.names?.en}
                    </span>
                    {formatEuro(selectedZone?.price)}
                  </Card.Text>
                </Card.Body>
              </Card>
            ) : null}

            <Form
              className="shipping-address-form"
              onSubmit={submitHandler}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            >
              <h2>{t('shipping-address-screen-heading')}</h2>

              <Form.Group controlId="fullNameShipping">
                <Form.Label>
                  {t('shipping-address-screen-full-name')}
                </Form.Label>
                <Form.Control
                  className="mobile-device-compatible"
                  value={fullNameShipping}
                  onChange={(e) => setFullNameShipping(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group controlId="companyNameShipping">
                <Form.Label>
                  {t('shipping-address-screen-company-name')}
                </Form.Label>
                <Form.Control
                  className="mobile-device-compatible"
                  value={companyNameShipping}
                  onChange={(e) => setCompanyNameShipping(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="emailShipping">
                <Form.Label>{t('shipping-address-screen-email')}</Form.Label>
                <Form.Control
                  type="email"
                  className="mobile-device-compatible"
                  value={emailShipping}
                  onChange={(e) => setEmailShipping(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group controlId="phoneShipping">
                <Form.Label>{t('shipping-address-screen-phone')}</Form.Label>
                <PhoneInput
                  inputClassName={`${phoneErrorShipping ? 'is-invalid' : ''}`}
                  defaultCountry="de"
                  forceDialCode
                  value={phoneShipping}
                  onChange={(phone, meta) => {
                    setPhoneShipping(phone);
                    setPhoneCountryShipping(meta?.country?.iso2 || 'de');
                    if (phoneErrorShipping) setPhoneErrorShipping(''); // clear error as user edits
                  }}
                  onBlur={() => {
                    if (!phoneShipping) return;
                    const check = validateAndNormalizePhone(
                      phoneShipping,
                      phoneCountryShipping
                    );
                    setPhoneErrorShipping(
                      check.ok ? '' : t('shipping-address-screen-phone-invalid')
                    );
                  }} // validate on blur for quicker feedback
                  inputProps={{
                    inputMode: 'tel',
                    autoComplete: 'tel',
                    name: 'phoneShipping',
                    id: 'phoneShipping',
                  }}
                />
                {phoneErrorShipping ? (
                  <div className="invalid-phone">{phoneErrorShipping}</div>
                ) : null}
              </Form.Group>

              {/* Country Search Input */}
              <Form.Group
                controlId="countrySearch"
                ref={shippingCountrySelectionRef}
              >
                <Form.Label>{t('shipping-address-screen-country')}</Form.Label>
                <Form.Control
                  type="text"
                  value={shippingCountrySearchTerm}
                  onChange={handleShippingCountrySearchChange}
                  onFocus={() => setShowShippingCountryList(true)}
                  placeholder={t('shipping-address-screen-country-search')}
                  className="mobile-device-compatible"
                  required
                />

                {/* Dropdown List */}
                <Dropdown show={showShippingCountryList}>
                  <Dropdown.Menu>
                    {filteredShippingCountries.map((country) => (
                      <Dropdown.Item
                        key={country._id}
                        onClick={() => handleSelectShippingCountry(country._id)}
                      >
                        {formatCountryLabel(
                          country.names?.[selectedLanguage] || {},
                          '???'
                        )}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>

              {countryIdShipping ? (
                <div>
                  <div className="street-info-container">
                    <Form.Group
                      className="address-street"
                      controlId="streetShipping"
                    >
                      <Form.Label>
                        {t('shipping-address-screen-street')}
                      </Form.Label>
                      <Autocomplete
                        onLoad={(ref) =>
                          (autocompleteShippingRef.current = ref)
                        }
                        onPlaceChanged={handlePlaceChangedShipping}
                        options={{
                          componentRestrictions: {
                            country:
                              selectedShippingCountry?.countryCode?.toLowerCase() ||
                              '',
                          }, // Only make address suggestions based on the selected country
                          types: ['address'], // Limit results to addresses only
                        }}
                      >
                        <Form.Control
                          className={`mobile-device-compatible ${
                            validationErrorsShipping.street && streetShipping
                              ? 'address-incorrect'
                              : ''
                          }`}
                          placeholder=""
                          value={streetShipping}
                          onChange={(e) => setStreetShipping(e.target.value)}
                          required
                        />
                      </Autocomplete>
                    </Form.Group>

                    <Form.Group
                      className="address-house-number"
                      controlId="houseNumberShipping"
                    >
                      <Form.Label>
                        {t('shipping-address-screen-house-number')}
                      </Form.Label>
                      <Form.Control
                        className={`mobile-device-compatible ${
                          validationErrorsShipping.houseNumber &&
                          houseNumberShipping
                            ? 'address-incorrect'
                            : ''
                        }`}
                        value={houseNumberShipping}
                        onChange={(e) => setHouseNumberShipping(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>

                  <Form.Group controlId="postalCodeShipping">
                    <Form.Label>
                      {t('shipping-address-screen-zip-code')}
                    </Form.Label>
                    <Form.Control
                      className={`mobile-device-compatible ${
                        validationErrorsShipping.postalCode &&
                        postalCodeShipping
                          ? 'address-incorrect'
                          : ''
                      }`}
                      value={postalCodeShipping}
                      onChange={(e) => setPostalCodeShipping(e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group controlId="cityShipping">
                    <Form.Label>{t('shipping-address-screen-city')}</Form.Label>
                    <Form.Control
                      className={`mobile-device-compatible ${
                        validationErrorsShipping.city && cityShipping
                          ? 'address-incorrect'
                          : ''
                      }`}
                      value={cityShipping}
                      onChange={(e) => setCityShipping(e.target.value)}
                      required
                    />
                  </Form.Group>

                  {/* Checkbox for "Billing address is the same as shipping address" */}
                  <Form.Group className="shipping-same-as-billing">
                    <Form.Check
                      type="checkbox"
                      id="billingSameAsShipping"
                      label={t(
                        'shipping-address-screen-shipping-same-as-billing'
                      )}
                      checked={billingSameAsShipping}
                      onChange={(e) =>
                        setBillingSameAsShipping(e.target.checked)
                      }
                    />
                  </Form.Group>
                </div>
              ) : null}

              {!billingSameAsShipping && countryIdShipping && (
                <div className="billing-address-divider">
                  <h2>{t('shipping-address-screen-billing-address')}</h2>

                  {/* Full Name */}
                  <Form.Group controlId="fullNameBilling">
                    <Form.Label>
                      {t('shipping-address-screen-full-name')}
                    </Form.Label>
                    <Form.Control
                      className="mobile-device-compatible"
                      value={fullNameBilling}
                      onChange={(e) => setFullNameBilling(e.target.value)}
                      required
                      disabled={billingSameAsShipping}
                    />
                  </Form.Group>

                  {/* Company Name */}
                  <Form.Group controlId="companyNameBilling">
                    <Form.Label>
                      {t('shipping-address-screen-company-name')}
                    </Form.Label>
                    <Form.Control
                      className="mobile-device-compatible"
                      value={companyNameBilling}
                      onChange={(e) => setCompanyNameBilling(e.target.value)}
                      disabled={billingSameAsShipping}
                    />
                  </Form.Group>

                  <Form.Group controlId="emailBilling">
                    <Form.Label>
                      {t('shipping-address-screen-email')}
                    </Form.Label>
                    <Form.Control
                      type="email"
                      className="mobile-device-compatible"
                      value={emailBilling}
                      onChange={(e) => setEmailBilling(e.target.value)}
                      required
                      disabled={billingSameAsShipping}
                    />
                  </Form.Group>

                  <Form.Group controlId="phoneBilling">
                    <Form.Label>
                      {t('shipping-address-screen-phone')}
                    </Form.Label>
                    <PhoneInput
                      inputClassName={`${
                        phoneErrorBilling ? 'is-invalid' : ''
                      }`}
                      defaultCountry="de"
                      forceDialCode
                      value={phoneBilling}
                      onChange={(phone, meta) => {
                        setPhoneBilling(phone);
                        setPhoneCountryBilling(meta?.country?.iso2 || 'de');
                        if (phoneErrorBilling) setPhoneErrorBilling(''); // clear error as user edits
                      }}
                      onBlur={() => {
                        if (!phoneBilling) return;
                        const check = validateAndNormalizePhone(
                          phoneBilling,
                          phoneCountryBilling
                        );
                        setPhoneErrorBilling(
                          check.ok
                            ? ''
                            : t('shipping-address-screen-phone-invalid')
                        );
                      }}
                      disabled={billingSameAsShipping}
                      inputProps={{
                        inputMode: 'tel',
                        autoComplete: 'tel',
                        name: 'phoneBilling',
                        id: 'phoneBilling',
                      }}
                    />
                    {phoneErrorBilling ? (
                      <div className="invalid-phone">{phoneErrorBilling}</div>
                    ) : null}
                  </Form.Group>

                  {/* Country Search Input */}
                  <Form.Group
                    controlId="countryBilling"
                    ref={billingCountrySelectionRef}
                  >
                    <Form.Label>
                      {t('shipping-address-screen-country')}
                    </Form.Label>
                    <Form.Control
                      className="mobile-device-compatible"
                      type="text"
                      value={billingCountrySearchTerm}
                      onChange={handleBillingCountrySearchChange}
                      onFocus={() => setShowBillingCountryList(true)}
                      placeholder={t('shipping-address-screen-country-search')}
                      disabled={billingSameAsShipping}
                      required
                    />

                    {/* Dropdown List */}
                    <Dropdown show={showBillingCountryList}>
                      <Dropdown.Menu>
                        {filteredBillingCountries.map((country) => (
                          <Dropdown.Item
                            key={country._id}
                            onClick={() =>
                              handleSelectBillingCountry(country._id)
                            }
                          >
                            {formatCountryLabel(
                              country.names?.[selectedLanguage] || {},
                              '???'
                            )}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>

                  {countryIdBilling ? (
                    <div>
                      <div className="street-info-container">
                        <Form.Group
                          className="address-street"
                          controlId="streetBilling"
                        >
                          <Form.Label>
                            {t('shipping-address-screen-street')}
                          </Form.Label>
                          <Autocomplete
                            onLoad={(ref) =>
                              (autocompleteBillingRef.current = ref)
                            }
                            onPlaceChanged={handlePlaceChangedBilling}
                            options={{
                              componentRestrictions: {
                                country:
                                  selectedBillingCountry?.countryCode?.toLowerCase() ||
                                  '',
                              }, // Only make address suggestions based on the selected country
                              types: ['address'], // Limit results to addresses only
                            }}
                          >
                            <Form.Control
                              className={`mobile-device-compatible ${
                                validationErrorsBilling.street && streetBilling
                                  ? 'address-incorrect'
                                  : ''
                              }`}
                              placeholder=""
                              value={streetBilling}
                              onChange={(e) => setStreetBilling(e.target.value)}
                              required
                              disabled={billingSameAsShipping}
                            />
                          </Autocomplete>
                        </Form.Group>
                        <Form.Group
                          className="address-house-number"
                          controlId="houseNumberBilling"
                        >
                          <Form.Label>
                            {t('shipping-address-screen-house-number')}
                          </Form.Label>
                          <Form.Control
                            className={`mobile-device-compatible ${
                              validationErrorsBilling.houseNumber &&
                              houseNumberBilling
                                ? 'address-incorrect'
                                : ''
                            }`}
                            value={houseNumberBilling}
                            onChange={(e) =>
                              setHouseNumberBilling(e.target.value)
                            }
                            required
                            disabled={billingSameAsShipping}
                          />
                        </Form.Group>
                      </div>

                      <Form.Group controlId="postalCodeBilling">
                        <Form.Label>
                          {t('shipping-address-screen-zip-code')}
                        </Form.Label>
                        <Form.Control
                          className={`mobile-device-compatible ${
                            validationErrorsBilling.postalCode &&
                            postalCodeBilling
                              ? 'address-incorrect'
                              : ''
                          }`}
                          value={postalCodeBilling}
                          onChange={(e) => setPostalCodeBilling(e.target.value)}
                          required
                          disabled={billingSameAsShipping}
                        />
                      </Form.Group>

                      <Form.Group controlId="cityBilling">
                        <Form.Label>
                          {t('shipping-address-screen-city')}
                        </Form.Label>
                        <Form.Control
                          className={`mobile-device-compatible ${
                            validationErrorsBilling.city && cityBilling
                              ? 'address-incorrect'
                              : ''
                          }`}
                          value={cityBilling}
                          onChange={(e) => setCityBilling(e.target.value)}
                          required
                          disabled={billingSameAsShipping}
                        />
                      </Form.Group>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="my-5 d-flex justify-content-center">
                <Button variant="secondary" type="submit">
                  {/* a Button component from bootstrap, that submits (type="submit") the Form from above automatically */}
                  {t('shipping-address-screen-continue')}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* ====== MODAL for Address Validation Errors ====== */}
      <Modal
        className="address-modal"
        show={showAddressValidationModal}
        onHide={() => setShowAddressValidationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {addressErrorType === 'apiError'
              ? t('shipping-address-screen-modal-header-1')
              : t('shipping-address-screen-modal-header-2')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {addressErrorType === 'apiError' && (
            <p>{t('shipping-address-screen-modal-error-message-1')}</p>
          )}
          {addressErrorType === 'both' && (
            <p>{t('shipping-address-screen-modal-error-message-2')}</p>
          )}
          {addressErrorType === 'shipping' && (
            <p>{t('shipping-address-screen-modal-error-message-3')}</p>
          )}
          {addressErrorType === 'billing' && (
            <p>{t('shipping-address-screen-modal-error-message-4')}</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          {addressErrorType === 'apiError' ? (
            <Button
              variant="dark"
              onClick={() => {
                setValidationBypassed(true);
                setShowAddressValidationModal(false);
              }}
            >
              {t('shipping-address-screen-modal-button-1')}
            </Button>
          ) : (
            <>
              <Button
                variant="dark"
                onClick={() => setShowAddressValidationModal(false)}
              >
                {t('shipping-address-screen-modal-button-2')}
              </Button>
              <Button variant="light" onClick={proceedSubmission}>
                {t('shipping-address-screen-modal-button-3')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
